import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  TrackByFunction,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngxs/store';
import { ActivitiesPipesModule } from '@wilson/activities/pipes';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  ACTIVITY_BORDER_PX,
  ACTIVITY_TITLE_HEIGHT_PX,
  ACTIVITY_TITLE_MARGIN_BOTTOM_PX,
  ACTIVITY_TITLE_MARGIN_TOP_PX,
  AccurateActivityTimeDetails,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  ItemViewModel,
  OperationStatus,
  ShiftCategoriesColorCode,
  TimelineShiftWithActivities,
} from '@wilson/interfaces';
import {
  ACTION_TOASTER_DURATION,
  NzMessageTimerLineComponent,
} from '@wilson/non-domain-specific/ng-zorro/nz-message';
import {
  DeferredShiftIconsHelperService,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
  IsShiftSelectedPipe,
  MessageWithUndoOptionComponent,
  SelectedItemOutput,
  ShiftCardTooltipStateService,
  ShiftMultiSelectHelperService,
  ShiftValidationTooltipWrapperComponent,
  TimeDifferenceWidthPxPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  GanttShiftEndTimePipe,
  GanttShiftStartTimePipe,
  ShiftTimelinePipesModule,
} from '@wilson/shift-timeline/pipes';
import { ChangedKeyManagerService } from '@wilson/shift-timeline/services';
import { ShiftTimelineDataState } from '@wilson/shift-timeline/state';
import { differenceInMilliseconds } from 'date-fns';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { TimelineAutoSizeActivitiesRowWithinShiftComponent } from './TimelineAutoSizeActivitiesRowWithinShift/timeline-auto-size-activities-row-within-shift.component';

@Component({
  selector: 'wilson-timeline-auto-size-shift-with-activities-card',
  templateUrl: './timeline-auto-size-shift-with-activities-card.component.html',
  styleUrls: ['./timeline-auto-size-shift-with-activities-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GanttShiftStartTimePipe,
    GanttShiftEndTimePipe,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
  ],
  imports: [
    CommonModule,
    ShiftTimelinePipesModule,
    NzTooltipModule,
    FontAwesomeModule,
    NzSkeletonModule,
    FeatureFlagsModule,
    TimelineAutoSizeActivitiesRowWithinShiftComponent,
    ShiftValidationTooltipWrapperComponent,
    TimeDifferenceWidthPxPipe,
    ActivitiesPipesModule,
    IsShiftSelectedPipe,
    MessageWithUndoOptionComponent,
    NzMessageTimerLineComponent,
  ],
})
export class TimelineAutoSizeShiftWithActivitiesCardComponent
  implements OnInit, AfterViewInit
{
  private readonly viewRef = inject(ViewContainerRef);
  readonly shiftCardTooltipStateService = inject(ShiftCardTooltipStateService);
  private readonly changedKeyManagerService = inject(ChangedKeyManagerService);
  readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private readonly store = inject(Store);
  protected deferredShiftIconsHelperService = inject(
    DeferredShiftIconsHelperService,
  );

  static count = 0;
  @ViewChild('shiftPreviewCard') shiftPreviewCardRef!: ElementRef;

  @Input() shift!: TimelineShiftWithActivities;
  @Input() shiftView!: ItemViewModel;
  @Input() classPositioning!: Record<string, boolean>;
  @Input() showShiftValidation = true;
  @Input() isShiftDraggable = true;
  @Input() userId!: string;
  @Input() parentElement!: HTMLElement | null;
  @Input() isDragging!: boolean;
  @Input() isCursorGrab!: boolean;
  @Input() isActivityDraggable!: boolean;
  @Input() filteredActivityId!: string | null;
  @Input() isShiftVisible: boolean | null = false;
  @Input() isTooltipVisible: boolean | null = false;
  @Input() set leftPixelPosition(value: number | null) {
    this.leftPixelPositionSubject.next(value);
  }
  @Input() rightPixelPosition: number | null = null;
  @Input() shiftStartDetail!: AccurateActivityTimeDetails;
  @Input() shiftEndDetail!: AccurateActivityTimeDetails;

  @Output() selectShift = new EventEmitter<SelectedItemOutput>();
  @Output() selectActivity = new EventEmitter<SelectedItemOutput>();
  @Output() previewRef = new EventEmitter<HTMLElement>();

  private leftPixelPositionSubject = new BehaviorSubject<number | null>(null);
  private isViewRenderedSubject = new BehaviorSubject(false);

  BORDER_SIZE = 4;
  isShiftValid = true;
  shiftCategoriesColorCode = ShiftCategoriesColorCode;

  ACTIVITY_TITLE_HEIGHT_PX = ACTIVITY_TITLE_HEIGHT_PX;
  ACTIVITY_TITLE_MARGIN_TOP_PX = ACTIVITY_TITLE_MARGIN_TOP_PX;
  ACTIVITY_TITLE_MARGIN_BOTTOM_PX = ACTIVITY_TITLE_MARGIN_BOTTOM_PX;
  ACTIVITY_BORDER_PX = ACTIVITY_BORDER_PX;
  MIN_WIDTH_PX = 110;
  accurateShiftDurationMs!: number;
  adjustedLeftPosition$!: Observable<number | null>;
  leftPixelPosition$ = this.leftPixelPositionSubject.asObservable();
  shiftWithActivitiesCount!: number;
  plannedShiftStartDateTime!: string;
  plannedShiftEndDateTime!: string;
  OperationStatus = OperationStatus;
  public readonly MESSAGE_DURATION = ACTION_TOASTER_DURATION;

  trackByActivityWithOverLevel: TrackByFunction<
    ActivityWithCategoryAndLocationsAndReportedDetails[]
  > = (
    _: number,
    activityWithOverlapLevel: ActivityWithCategoryAndLocationsAndReportedDetails[],
  ) => {
    return this.changedKeyManagerService.computeChangedKeyForActivities(
      activityWithOverlapLevel,
    );
  };
  public shiftIdsToHighlight$ = this.store.select(
    ShiftTimelineDataState.shiftIdsToHighlight,
  );

  ngOnInit(): void {
    this.plannedShiftStartDateTime =
      this.shiftView.shift.activities[0].startDatetime;
    this.plannedShiftEndDateTime =
      this.shiftView.shift.activities[
        this.shiftView.shift.activities.length - 1
      ].endDatetime;
    this.accurateShiftDurationMs = differenceInMilliseconds(
      new Date(this.shiftEndDetail.date),
      new Date(this.shiftStartDetail.date),
    );

    this.adjustedLeftPosition$ = combineLatest([
      this.isViewRenderedSubject,
      this.leftPixelPosition$,
    ]).pipe(
      map(
        ([isViewRendered, supposedLeftPosition]: [boolean, number | null]) => {
          let adjustedLeftPosition = supposedLeftPosition;
          if (
            isViewRendered &&
            this.parentElement &&
            supposedLeftPosition !== null
          ) {
            const parentRect = this.parentElement.getBoundingClientRect();
            const renderedRect =
              this.viewRef.element.nativeElement.getBoundingClientRect();
            const errorAmount = renderedRect.left - parentRect.left;
            adjustedLeftPosition = supposedLeftPosition - errorAmount;
          }

          return adjustedLeftPosition;
        },
      ),
    );
  }

  ngAfterViewInit(): void {
    this.isViewRenderedSubject.next(true);

    this.previewRef.emit(this.shiftPreviewCardRef.nativeElement);
  }

  async checkShiftKeyOnClick(
    event: MouseEvent,
    shiftId: string,
    leftPixelPosition: number | null,
    rightPixelPosition: number | null,
    undoTemplate: TemplateRef<void>,
  ): Promise<void> {
    await this.shiftMultiSelectHelperService.handleClick({
      shiftId,
      event,
      leftPixelPosition,
      rightPixelPosition,
      singleShiftSelectedCallback: this.selectShift.emit.bind(this.selectShift),
      templateRef: undoTemplate,
      messageDuration: this.MESSAGE_DURATION,
      isUnAssignedShift: false,
    });
  }
}
